import type { ButtonHTMLAttributes, ReactNode } from 'react'

// ───── Button ─────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

const buttonVariantClass: Record<ButtonVariant, string> = {
  primary:
    'bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] hover:bg-[color:var(--color-orion-amber-dim)] active:translate-y-px',
  secondary:
    'bg-[color:var(--color-orion-night)] text-[color:var(--color-orion-text)] border-faint hover:bg-[color:var(--color-orion-surface)]',
  ghost:
    'bg-transparent text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)]',
  danger:
    'bg-[color:var(--color-orion-block)] text-[color:var(--color-orion-text)] hover:opacity-90',
}

const buttonSizeClass: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-[length:var(--text-small)]',
  md: 'px-4 py-2 text-[length:var(--text-body)]',
  lg: 'px-6 py-3 text-[length:var(--text-lead)]',
}

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-[var(--radius-md)] font-medium transition-all duration-150 ease-[var(--ease-orion)] disabled:opacity-50 disabled:cursor-not-allowed ${buttonVariantClass[variant]} ${buttonSizeClass[size]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  )
}

// ───── Badge ─────

type BadgeTone = 'pass' | 'warn' | 'block' | 'info' | 'process' | 'neutral'

interface BadgeProps {
  tone?: BadgeTone
  dot?: boolean
  children: ReactNode
  className?: string
}

const badgeToneClass: Record<BadgeTone, { bg: string; fg: string; dot: string }> = {
  pass: {
    bg: 'bg-[color:oklch(70%_0.16_145/0.15)]',
    fg: 'text-[color:var(--color-orion-pass)]',
    dot: 'bg-[color:var(--color-orion-pass)]',
  },
  warn: {
    bg: 'bg-[color:oklch(78%_0.14_85/0.15)]',
    fg: 'text-[color:var(--color-orion-warn)]',
    dot: 'bg-[color:var(--color-orion-warn)]',
  },
  block: {
    bg: 'bg-[color:oklch(62%_0.22_25/0.18)]',
    fg: 'text-[color:var(--color-orion-block)]',
    dot: 'bg-[color:var(--color-orion-block)]',
  },
  info: {
    bg: 'bg-[color:oklch(70%_0.12_230/0.15)]',
    fg: 'text-[color:var(--color-orion-info)]',
    dot: 'bg-[color:var(--color-orion-info)]',
  },
  process: {
    bg: 'bg-[color:oklch(68%_0.16_290/0.15)]',
    fg: 'text-[color:var(--color-orion-violet)]',
    dot: 'bg-[color:var(--color-orion-violet)]',
  },
  neutral: {
    bg: 'bg-[color:oklch(58%_0.03_250/0.18)]',
    fg: 'text-[color:var(--color-orion-haze)]',
    dot: 'bg-[color:var(--color-orion-haze)]',
  },
}

export function Badge({ tone = 'neutral', dot = false, children, className = '' }: BadgeProps) {
  const t = badgeToneClass[tone]
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2 py-0.5 text-[length:var(--text-micro)] uppercase tracking-[0.08em] font-medium ${t.bg} ${t.fg} ${className}`}
    >
      {dot && <span className={`size-1.5 rounded-full ${t.dot}`} />}
      {children}
    </span>
  )
}

// ───── Pill (segmented toggle) ─────

interface PillOption<T extends string> {
  value: T
  label: string
}

interface PillProps<T extends string> {
  options: PillOption<T>[]
  value: T
  onChange: (v: T) => void
  ariaLabel?: string
  className?: string
}

export function Pill<T extends string>({ options, value, onChange, ariaLabel, className = '' }: PillProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-1 rounded-full border-faint p-1 bg-[color:var(--color-orion-deep)]/60 backdrop-blur-sm ${className}`}
    >
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1 text-[length:var(--text-small)] rounded-full transition-all duration-150 ease-[var(--ease-orion)] ${
              active
                ? 'bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium'
                : 'text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)]'
            }`}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// ───── Card ─────

interface CardProps {
  children: ReactNode
  glow?: 'amber' | 'block' | 'none'
  className?: string
}

export function Card({ children, glow = 'none', className = '' }: CardProps) {
  const glowClass = glow === 'amber' ? 'glow-amber' : glow === 'block' ? 'glow-block' : ''
  return (
    <div
      className={`surface-night border-faint rounded-[var(--radius-lg)] p-6 ${glowClass} ${className}`}
    >
      {children}
    </div>
  )
}

// ───── Tag ─────

interface TagProps {
  children: ReactNode
  kind?: 'standard' | 'rule' | 'entity' | 'plain'
  className?: string
}

const tagKindClass: Record<NonNullable<TagProps['kind']>, string> = {
  standard:
    'border border-[color:oklch(75%_0.12_220/0.4)] text-[color:var(--color-orion-cyan)]',
  rule:
    'border border-[color:oklch(75%_0.16_65/0.5)] text-[color:var(--color-orion-amber)]',
  entity:
    'border border-[color:oklch(68%_0.16_290/0.4)] text-[color:var(--color-orion-violet)]',
  plain:
    'border-faint text-[color:var(--color-orion-haze)]',
}

export function Tag({ children, kind = 'plain', className = '' }: TagProps) {
  return (
    <span
      className={`mono inline-flex items-center px-2 py-0.5 text-[length:var(--text-micro)] rounded-[var(--radius-xs)] tracking-wider ${tagKindClass[kind]} ${className}`}
    >
      {children}
    </span>
  )
}
